import { useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { biometricSettings, stepConfigItem } from '../types';
import { dietSettingsIsLastStep } from '../utils/DietSettingsIsLastStep';
import { dietSettingsGetNextStep } from '../utils/DietSettingsNextStepAction';
import { dietSettingsGetPreviousStep } from '../utils/DietSettingsPreviousStepAction';
import { dietSettingsGetSchemaForParameters } from '../utils/DietSettingsSchemaForParametersAction';
import StepContext from './StepContext';

interface StepProviderProps {
  currentMainStep: string;
  schema: stepConfigItem[];
  children: React.ReactNode;
  saveUserData: (values: biometricSettings) => void;
  validateGoalSettings: (values: biometricSettings) => void;
}

const StepProvider: React.FC<StepProviderProps> = ({
  currentMainStep,
  schema,
  children,
  saveUserData,
  validateGoalSettings,
}) => {
  const params = useParams();
  const stepFromUrl = params['*'];
  const { values: biometricSettings } = useFormikContext<biometricSettings>();
  const schemaForParameters = dietSettingsGetSchemaForParameters(schema, biometricSettings);

  const [currentStep, setCurrentStep] = useState(
    schemaForParameters.find((step) => step.slug === stepFromUrl)?.id || ''
  );

  useEffect(() => {
    setCurrentStep(schemaForParameters.find((step) => step.slug === stepFromUrl)?.id || '');
  }, [params, stepFromUrl, schema, schemaForParameters]);

  const prevStep = dietSettingsGetPreviousStep(schemaForParameters, biometricSettings, currentStep);
  const nextStep = dietSettingsGetNextStep(schemaForParameters, biometricSettings, currentStep);
  const isLastStep = dietSettingsIsLastStep(schema, biometricSettings, currentStep);
  const shouldBackendValidate = schemaForParameters.find(
    (step) => step.slug === stepFromUrl
  )?.shouldBackendValidate;

  return (
    <StepContext.Provider
      value={{
        currentStep: currentStep,
        currentMainStep: currentMainStep,
        schema: schema,
        prevStep: prevStep,
        nextStep: nextStep,
        isLastStep: isLastStep,
        shouldBackendValidate: shouldBackendValidate,
        saveUserData: saveUserData,
        validateGoalSettings: validateGoalSettings,
      }}
    >
      {children}
    </StepContext.Provider>
  );
};

export default StepProvider;
